import { useState, useCallback, useRef } from 'react'
import {
  createUpload,
  uploadToPresigned,
  confirmUpload,
  failUpload,
  ApiError,
  UploadOptionsApi
} from '../../../services/api'
import { contextualErrors, FriendlyError } from '../../../services/errorHandler'
import { UploadOptions } from '../types/upload.types'
import { BETA_LIMITS } from '../../../utils/betaLimits'
import { useBetaMode } from '../../../contexts'

// Executa até 'limit' promessas em paralelo, mantendo a ordem dos resultados
async function runWithConcurrency<T, R>(
  items: T[],
  limit: number,
  fn: (item: T, index: number) => Promise<R>
): Promise<R[]> {
  const results: R[] = new Array(items.length)
  let nextIndex = 0
  async function worker() {
    while (nextIndex < items.length) {
      const i = nextIndex++
      results[i] = await fn(items[i], i)
    }
  }
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker))
  return results
}

export type UploadStep = 'idle' | 'creating' | 'uploading' | 'confirming'

export interface UploadingFile {
  file: File
  status: UploadStep
  progress: number
  error: string | null
  warning: string | null
  fileId: string | null
}

interface UseUploadReturn {
  // Estado
  uploadingFiles: UploadingFile[]
  pendingFiles: File[]
  showConfirmation: boolean
  isUploading: boolean

  // Actions
  selectFiles: (files: FileList | null) => void
  confirmUpload: (files: File[], options: UploadOptions) => Promise<{ success: number; failed: number }>
  cancelUpload: () => void
  removePendingFile: (index: number) => void
  clearCompleted: () => void
}

export function useUpload(onUploadComplete?: () => void): UseUploadReturn {
  const betaMode = useBetaMode()
  const [uploadingFiles, setUploadingFiles] = useState<UploadingFile[]>([])
  const [pendingFiles, setPendingFiles] = useState<File[]>([])
  const [showConfirmation, setShowConfirmation] = useState(false)

  const isUploading = uploadingFiles.some(f => f.status !== 'idle' && !f.error)

  // Seleciona arquivos e abre modal de confirmação
  const selectFiles = useCallback((fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) return
    setPendingFiles(Array.from(fileList))
    setShowConfirmation(true)
  }, [])

  // Confirma upload com opções
  const handleConfirmUpload = useCallback(async (
    files: File[],
    options: UploadOptions
  ): Promise<{ success: number; failed: number }> => {
    setShowConfirmation(false)
    setPendingFiles([])

    // Gera um ID único para todo o lote — o backend usa para saber quando
    // TODOS os arquivos do batch terminaram e dispara um único email.
    const uploadBatchId = crypto.randomUUID()

    const apiOptions: UploadOptionsApi = {
      folderId: options.folderId,
      enableAnalysis: options.enableAnalysis,
      selectedAnalyses: options.selectedAnalyses,
      notifyByEmail: options.notifyByEmail,
      notificationEmail: options.notificationEmail,
      retainMedia: options.retainMedia,
      uploadBatchId,
      totalFiles: files.length
    }

    // Inicializa estado de cada arquivo
    const initialStates: UploadingFile[] = files.map(file => ({
      file,
      status: 'creating' as UploadStep,
      progress: 0,
      error: null,
      warning: null,
      fileId: null
    }))
    setUploadingFiles(initialStates)

    // Processa uploads respeitando o limite de concorrência beta
    const concurrencyLimit = betaMode ? BETA_LIMITS.maxConcurrentUploads : Infinity
    const results = await runWithConcurrency(
      files,
      concurrencyLimit,
      async (file, index) => {
        let createdId: string | null = null

        try {
          // Creating
          setUploadingFiles(prev =>
            prev.map((uf, i) => (i === index ? { ...uf, status: 'creating', progress: 10 } : uf))
          )

          const create = await createUpload(file, apiOptions)
          createdId = create.fileId

          setUploadingFiles(prev =>
            prev.map((uf, i) => (i === index ? { ...uf, fileId: createdId, status: 'uploading', progress: 15 } : uf))
          )

          // Uploading com progress tracking
          await uploadToPresigned(create.presignedUrl, create.headers, file, (progress) => {
            // Mapeia progress de upload (0-100) para o range 15-70 do progresso total
            const mappedProgress = 15 + (progress * 0.55)
            setUploadingFiles(prev =>
              prev.map((uf, i) => (i === index ? { ...uf, progress: Math.round(mappedProgress) } : uf))
            )
          })

          setUploadingFiles(prev =>
            prev.map((uf, i) => (i === index ? { ...uf, status: 'confirming', progress: 75 } : uf))
          )

          // Confirming — no v2, a transcrição é iniciada automaticamente pelo backend
          await confirmUpload(create.fileId)

          setUploadingFiles(prev =>
            prev.map((uf, i) => (i === index ? { ...uf, status: 'idle', progress: 100 } : uf))
          )

          return { success: true }
        } catch (e: any) {
          // Se o backend retornou WARN (ex: NO_AUDIO_DETECTED), é um estado terminal de warning,
          // não um erro fatal. Não chama failUpload (backend já marcou como WARNING).
          const isBackendWarning = e instanceof ApiError && e.code === 'WARN'

          if (isBackendWarning) {
            setUploadingFiles(prev =>
              prev.map((uf, i) => (i === index ? { ...uf, warning: e.message, error: null, status: 'idle', progress: 100 } : uf))
            )
            return { success: false }
          }

          const friendlyError = contextualErrors.upload(e)

          setUploadingFiles(prev =>
            prev.map((uf, i) => (i === index ? { ...uf, error: friendlyError.message, warning: null, progress: 0 } : uf))
          )

          if (createdId) {
            try {
              await failUpload(createdId)
            } catch {}
          }

          return { success: false }
        }
      }
    )

    // Limpa após 3 segundos
    setTimeout(() => setUploadingFiles([]), 3000)

    const successCount = results.filter(r => r.success).length
    const failCount = results.filter(r => !r.success).length

    if (successCount > 0 && onUploadComplete) {
      onUploadComplete()
    }

    return { success: successCount, failed: failCount }
  }, [onUploadComplete])

  // Cancela upload pendente
  const cancelUpload = useCallback(() => {
    setShowConfirmation(false)
    setPendingFiles([])
  }, [])

  // Remove um arquivo individual da fila de confirmação
  const removePendingFile = useCallback((index: number) => {
    setPendingFiles(prev => {
      const next = prev.filter((_, i) => i !== index)
      if (next.length === 0) {
        setShowConfirmation(false)
      }
      return next
    })
  }, [])

  // Limpa uploads completos
  const clearCompleted = useCallback(() => {
    setUploadingFiles(prev => prev.filter(f => f.status !== 'idle'))
  }, [])

  return {
    uploadingFiles,
    pendingFiles,
    showConfirmation,
    isUploading,
    selectFiles,
    confirmUpload: handleConfirmUpload,
    cancelUpload,
    removePendingFile,
    clearCompleted
  }
}

export default useUpload
